type Route = {
  path: string;
  title?: string;
  children?: Record<string, Route>;
};

type ParsedRoute<T> = T extends { children: infer C }
  ? ParsedRouteProps & { [K in keyof C]: ParsedRoute<C[K]> }
  : ParsedRouteProps;

type RouteParams = Record<string, string | number | undefined | null>;

type ParsedRouteProps = {
  title?: string;
  route: string; // Get react-router relative route path for route rendering
  getAbsolute: (params?: RouteParams) => string; // Get absolute path with parameters for navigation
};

function parse<T extends Record<string, Route>>(
  routes: T,
  basePath: string = '',
): { [K in keyof T]: ParsedRoute<T[K]> } {
  const parsedRoutes: Record<string, unknown> = {};

  Object.keys(routes).forEach((key) => {
    const route = routes[key];
    const currentPath = `${basePath}/${route.path}`;

    const hasChildren = Object.keys(route.children || {}).length > 0;

    const children = route.children ? parse(route.children, currentPath) : {}; // Recursively parse children

    const routeValue: ParsedRoute<typeof route> = {
      title: route.title,
      route: getRoutePath(route.path, {}, hasChildren),
      getAbsolute: (params?: RouteParams) => getRoutePath(currentPath, params),
      ...children,
    };

    parsedRoutes[key] = routeValue;
  });

  return parsedRoutes as { [K in keyof T]: ParsedRoute<T[K]> };
}

function getRoutePath(path: string, params?: RouteParams, hasChildren?: boolean) {
  if (!params) return path;

  let resolvedPath = Object.keys(params).reduce((acc, key) => {
    const queryParam = (params[key] || key).toString();

    return acc.replace(new RegExp(`:${key}`, 'g'), queryParam);
  }, path);

  if (hasChildren) resolvedPath += '/*';

  return resolvedPath;
}

export { parse };
